// =============================================================================
// src/socket.js  —  Dashboard's Socket.IO client (singleton)
//
// One shared socket connection across the whole app. Lazy-connects on first
// subscription. Auto-fetches a fresh JWT from POST /auth/socket-token before
// connecting, and re-fetches if the connection drops with auth_invalid (token
// expired during a long session).
//
// API:
//   getSocket()          — returns the singleton, connects if needed
//   onEvent(event, fn)   — subscribe; returns unsubscribe function
//   isConnected()        — boolean
//
// Usage:
//   import { onEvent } from '../socket';
//   useEffect(() => {
//     const off = onEvent('sync:complete', (payload) => { ... });
//     return off;
//   }, []);
// =============================================================================

import { io } from 'socket.io-client';

const BASE = process.env.REACT_APP_API_URL || 'http://localhost:3000';

let socket = null;
let connectingPromise = null;
let cachedToken = null;
let cachedTokenExpiresAt = 0;

function getAuthHeaders() {
  try {
    const saved = localStorage.getItem('auditpros_user');
    if (!saved) return {};
    const parsed = JSON.parse(saved);
    const account = parsed?.account;
    const email =
         account?.idTokenClaims?.email
      || account?.idTokenClaims?.preferred_username
      || account?.idTokenClaims?.emails?.[0]
      || account?.username || account?.email;
    const h = { 'Content-Type': 'application/json' };
    if (email) h['x-user-email'] = email;
    return h;
  } catch { return {}; }
}

async function fetchSocketToken() {
  // Return cached if valid (with 5-min safety margin before expiry)
  if (cachedToken && cachedTokenExpiresAt - Date.now() > 5 * 60 * 1000) {
    return cachedToken;
  }

  const res = await fetch(`${BASE}/users/auth/socket-token`, {
    method: 'POST',
    headers: getAuthHeaders(),
  });
  if (!res.ok) {
    throw new Error(`socket-token request failed: ${res.status}`);
  }
  const { token, expiresIn } = await res.json();
  cachedToken = token;
  cachedTokenExpiresAt = Date.now() + (expiresIn * 1000);
  return token;
}

async function _connect() {
  const token = await fetchSocketToken();

  socket = io(BASE, {
    auth: { socketToken: token },
    transports: ['websocket'],
    reconnection: true,
    reconnectionAttempts: Infinity,
    reconnectionDelay: 2000,
    reconnectionDelayMax: 30_000,
    timeout: 15_000,
  });

  socket.on('connect', () => {
    console.log('[dashboard-socket] connected', socket.id);
  });

  socket.on('dashboard:connected', (payload) => {
    console.log('[dashboard-socket] server ack', payload);
  });

  socket.on('disconnect', (reason) => {
    console.log('[dashboard-socket] disconnected:', reason);
  });

  socket.on('connect_error', async (err) => {
    const msg = String(err?.message || err);
    console.warn('[dashboard-socket] connect_error:', msg);

    // If auth failed, refresh token and reconnect once
    if (msg === 'auth_invalid' || msg === 'auth_required') {
      cachedToken = null;
      cachedTokenExpiresAt = 0;
      try {
        const fresh = await fetchSocketToken();
        socket.auth = { socketToken: fresh };
        // Socket.io will retry automatically; new auth applies on next attempt
      } catch (e) {
        console.error('[dashboard-socket] failed to refresh token:', e.message);
      }
    }
  });

  return socket;
}

export async function getSocket() {
  if (socket?.connected) return socket;
  if (socket && !socket.connected) return socket;  // mid-reconnect
  if (connectingPromise) return connectingPromise;

  connectingPromise = _connect()
    .catch(err => {
      console.error('[dashboard-socket] initial connect failed:', err.message);
      socket = null;
      connectingPromise = null;
      throw err;
    })
    .finally(() => { connectingPromise = null; });

  return connectingPromise;
}

/**
 * Subscribe to an event. Returns an unsubscribe function.
 * Lazily ensures the socket is connected.
 */
export function onEvent(event, handler) {
  let active = true;

  getSocket()
    .then(s => {
      if (!active) return;
      s.on(event, handler);
    })
    .catch(() => {
      // Already logged in _connect; stay silent here so callers don't get
      // unhandled rejections.
    });

  return () => {
    active = false;
    if (socket) socket.off(event, handler);
  };
}

export function isConnected() {
  return !!socket?.connected;
}
